import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import { FiDownload, FiEye, FiTrash2 } from "react-icons/fi";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  downloadBlobResponse,
  formatDateOnlyIST,
  formatDateTimeIST,
  formatFileSize,
  getApiErrorMessage,
  getApiErrorMessageAsync,
  getFitnessPillStyle,
  openBlobResponse,
} from "../hrRecordsHelpers";
import "../../Views/ApprovedPrView.css";
import "../../Views/voyageView.css";
import "../hrRecords.css";

function MedicalReportViewPage({ isSidebarExpanded }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const run = async () => {
      try {
        const response = await hrRecordsApi.getMedicalReport(id);
        setReport(response);
      } catch (error) {
        console.error("Failed to load medical report:", error);
        showSnackbar("Failed to load medical report", "error");
      }
    };
    run();
  }, [id]);

  const handleFileAction = async (request, filename, action = "download") => {
    try {
      const response = await request;
      if (action === "view") {
        openBlobResponse(response);
      } else {
        downloadBlobResponse(response, filename);
      }
    } catch (error) {
      console.error("File action failed:", error);
      showSnackbar(
        await getApiErrorMessageAsync(error, "File action failed"),
        "error",
      );
    }
  };

  const handleDeleteAttachment = async (fileId) => {
    if (!window.confirm("Delete this attachment?")) return;

    try {
      await hrRecordsApi.deleteMedicalAttachment(fileId);
      setReport((prev) => ({
        ...prev,
        attachments: (prev.attachments || []).filter(
          (file) => file.file_id !== fileId,
        ),
      }));
      showSnackbar("Attachment deleted successfully");
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to delete attachment"),
        "error",
      );
    }
  };

  const handleDownloadAllForReport = () => {
    const namePart = (report.person_name || "Employee").replace(/\s+/g, "_");
    handleFileAction(
      hrRecordsApi.downloadAllMedicalAttachments(id),
      `${namePart}_${report.medical_type || "Medical"}_Reports.zip`,
    );
  };

  const handleDownloadAllForEmployee = () => {
    if (!report.actual_man_power_id) return;
    const namePart = (report.person_name || "Employee").replace(/\s+/g, "_");
    handleFileAction(
      hrRecordsApi.downloadAllEmployeeMedicalReports(report.actual_man_power_id),
      `${namePart}_All_Medical_Reports.zip`,
    );
  };

  return (
    <>
      <div
        className={`invoice-request-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="invoice-request-header-content">
          <div className="invoice-request-heading-content-left-side">
            <Tooltip title="Back" arrow>
              <IoMdArrowBack
                className="back-company"
                onClick={() => navigate("/hr-medical-report-list")}
              />
            </Tooltip>
            Medical Report Details
          </div>
          {report?.actual_man_power_id && (
            <div className="hr-records-inline-actions">
              <button className="button-bg" onClick={handleDownloadAllForEmployee}>
                <FiDownload /> Download All (Employee)
              </button>
            </div>
          )}
        </div>

        <div className="data-form-body-voyage">
          {!report ? (
            <div className="loader-container">
              <div className="loader"></div>
              <div>Loading...</div>
            </div>
          ) : (
            <>
              <div className="project-detailss">
                <div className="project-detail-arround">
                  <div className="project-details-left-side">
                    {[
                      ["Person Name", report.person_name],
                      ["EC No.", report.ec_no],
                      ["Medical Type", report.medical_type],
                      ["Examination Date", formatDateOnlyIST(report.examination_date)],
                      ["Report Number", report.report_number],
                      ["Medical Center", report.medical_center],
                      ["Examining Doctor", report.examining_doctor],
                      ["Valid From", formatDateOnlyIST(report.valid_from)],
                      ["Valid To", formatDateOnlyIST(report.valid_to)],
                      ["Next Due Date", formatDateOnlyIST(report.next_due_date)],
                      ["Project", report.project_name],
                      ["Created At", formatDateTimeIST(report.created_at)],
                    ].map(([label, value]) => (
                      <div className="project-detail" key={label}>
                        <div className="detail-header">{label}:</div>
                        <div className="detail-part-voyage">{value || "N/A"}</div>
                      </div>
                    ))}

                    <div className="project-detail">
                      <div className="detail-header">Fitness Status:</div>
                      <div className="detail-part-voyage">
                        <span
                          className="hr-records-pill"
                          style={getFitnessPillStyle(report.fitness_status)}
                        >
                          {report.fitness_status || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <br />

              <div className="hr-records-section">
                <div
                  className="hr-records-section-title"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  Attachments
                  {report.attachments?.length > 0 && (
                    <button className="button-bg" onClick={handleDownloadAllForReport}>
                      <FiDownload /> Download All
                    </button>
                  )}
                </div>
                {report.attachments?.length ? (
                  <div className="hr-records-file-list">
                    {report.attachments.map((file) => (
                      <div className="hr-records-file-card" key={file.file_id}>
                        <div>
                          <div className="hr-records-file-title">{file.filename}</div>
                          <div className="hr-records-file-meta">
                            {formatFileSize(file.file_size)}
                          </div>
                        </div>
                        <div className="hr-records-inline-actions">
                          <button
                            className="file-action-btn"
                            onClick={() =>
                              handleFileAction(
                                hrRecordsApi.viewMedicalAttachment(file.file_id),
                                file.filename,
                                "view",
                              )
                            }
                          >
                            <FiEye /> View
                          </button>
                          <button
                            className="file-action-btn"
                            onClick={() =>
                              handleFileAction(
                                hrRecordsApi.downloadMedicalAttachment(file.file_id),
                                file.filename,
                              )
                            }
                          >
                            <FiDownload /> Download
                          </button>
                          <button
                            className="file-action-btn"
                            onClick={() => handleDeleteAttachment(file.file_id)}
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="hr-records-empty">No attachments found.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default MedicalReportViewPage;
